import { useEffect, useState } from 'react';
import { FormAkunAi } from '@/components/ai/FormAkunAi';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BrainCircuit,
  Copy,
  KeyRound,
  LogOut,
  MailCheck,
  Monitor,
  Moon,
  Plus,
  RefreshCw,
  ShieldCheck,
  Sun,
  Trash2,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useThemeStore } from '@/store/theme';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input, Toggle, Select } from '@/components/ui/Input';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { EmptyState } from '@/components/ui/States';
import { Badge } from '@/components/ui/Badge';
import { initials, formatDateTime, formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';

const TABS = [
  ['profil', 'Profil'],
  ['tampilan', 'Tampilan'],
  ['keamanan', 'Keamanan'],
  ['notifikasi', 'Notifikasi'],
  ['api', 'API Key'],
  ['ai', 'AI'],
];

export const SettingsPage = () => {
  const [tab, setTab] = useState('profil');

  return (
    <div className="grid gap-5 lg:grid-cols-[200px_1fr]">
      {/* Navigasi tab: menyamping di layar lebar, menggulir di layar sempit. */}
      <nav
        aria-label="Bagian pengaturan"
        className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible"
      >
        {TABS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-current={tab === value ? 'page' : undefined}
            onClick={() => setTab(value)}
            className={cn(
              'shrink-0 rounded-lg px-3.5 py-2.5 text-left text-sm font-semibold transition-colors',
              tab === value
                ? 'bg-[var(--surface-raised)] text-[var(--text-primary)] shadow-xs'
                : 'text-[var(--text-muted)] hover:bg-[var(--surface-raised)]/60',
            )}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="space-y-5">
        {tab === 'profil' && <ProfileSection />}
        {tab === 'tampilan' && <AppearanceSection />}
        {tab === 'keamanan' && (
          <>
            <SecuritySection />
            <SessionSection />
          </>
        )}
        {tab === 'notifikasi' && <NotificationSection />}
        {tab === 'api' && <ApiKeySection />}
        {tab === 'ai' && <AiKeySection />}
      </div>
    </div>
  );
};

const ProfileSection = () => {
  const { user, updateUser } = useAuthStore();
  const toast = useToast();

  const form = useForm({ defaultValues: { name: user?.name ?? '', email: user?.email ?? '' } });

  const mutation = useMutation({
    mutationFn: (values) => api.patch('/me', { name: values.name }),
    onSuccess: (response) => {
      updateUser(response.data);
      toast.success('Profil diperbarui');
    },
    onError: (err) => toast.error('Gagal menyimpan', { description: err.message }),
  });

  return (
    <Card>
      <CardHeader title="Profil" />
      <CardBody className="space-y-5">
        <VerifikasiEmail />

        <div className="flex items-center gap-4">
          <div className="grid size-14 place-items-center rounded-full bg-brand-950 text-lg font-bold text-white dark:bg-accent-600">
            {initials(user?.name)}
          </div>
          <Button variant="secondary" size="sm">
            Ganti Foto
          </Button>
        </div>

        <form
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          className="grid gap-4 sm:grid-cols-2"
        >
          <Field label="Nama Lengkap">
            {(props) => <Input {...props} {...form.register('name')} />}
          </Field>

          <Field label="Email" hint="Email tidak bisa diubah dari halaman ini.">
            {(props) => <Input {...props} {...form.register('email')} disabled />}
          </Field>

          <div className="sm:col-span-2">
            <Button type="submit" loading={mutation.isPending}>
              Simpan Perubahan
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

/** Bagian tampilan - mode gelap adalah tambahan yang tidak ada di rancangan. */
const AppearanceSection = () => {
  const { theme, setTheme } = useThemeStore();

  const options = [
    ['light', 'Terang', Sun],
    ['dark', 'Gelap', Moon],
    ['system', 'Ikuti Sistem', Monitor],
  ];

  return (
    <Card>
      <CardHeader
        title="Tampilan"
        description="Pilihan ini tersimpan di peramban Anda."
      />
      <CardBody>
        <div className="grid gap-3 sm:grid-cols-3">
          {options.map(([value, label, Icon]) => (
            <button
              key={value}
              type="button"
              onClick={() => setTheme(value)}
              aria-pressed={theme === value}
              className={cn(
                'flex flex-col items-center gap-2 rounded-xl border-2 px-4 py-5 transition-colors',
                theme === value
                  ? 'border-brand-950 bg-[var(--surface-sunken)] dark:border-accent-500'
                  : 'border-[var(--border-subtle)] hover:border-[var(--border-default)]',
              )}
            >
              <Icon className="size-5 text-[var(--text-secondary)]" aria-hidden="true" />
              <span className="text-sm font-semibold text-[var(--text-primary)]">{label}</span>
            </button>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

const SecuritySection = () => {
  const toast = useToast();
  const form = useForm();

  const mutation = useMutation({
    mutationFn: (values) => api.post('/auth/change-password', values),
    onSuccess: () => {
      toast.success('Kata sandi diubah', {
        description: 'Sesi di perangkat lain sudah dikeluarkan.',
      });
      form.reset();
    },
    onError: (err) => form.setError('root', { message: err.message }),
  });

  return (
    <Card>
      <CardHeader
        title="Kata Sandi"
        description="Mengubah kata sandi akan mengeluarkan sesi di semua perangkat lain."
      />
      <CardBody>
        <form
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          className="max-w-sm space-y-4"
        >
          <Field label="Kata Sandi Saat Ini" required>
            {(props) => (
              <Input
                {...props}
                {...form.register('currentPassword', { required: true })}
                type="password"
                autoComplete="current-password"
              />
            )}
          </Field>

          <Field label="Kata Sandi Baru" required hint="Minimal 8 karakter.">
            {(props) => (
              <Input
                {...props}
                {...form.register('newPassword', { required: true, minLength: 8 })}
                type="password"
                autoComplete="new-password"
              />
            )}
          </Field>

          {form.formState.errors.root && (
            <p role="alert" className="text-sm font-medium text-danger-600">
              {form.formState.errors.root.message}
            </p>
          )}

          <Button type="submit" loading={mutation.isPending}>
            Ubah Kata Sandi
          </Button>
        </form>
      </CardBody>
    </Card>
  );
};

const NotificationSection = () => {
  const { user, updateUser } = useAuthStore();
  const toast = useToast();
  const prefs = user?.preferences?.notifications ?? {};

  const mutation = useMutation({
    mutationFn: (notifications) => api.patch('/me', { preferences: { notifications } }),
    onSuccess: (response) => updateUser(response.data),
    onError: (err) => toast.error('Gagal menyimpan', { description: err.message }),
  });

  const update = (key, value) => {
    const next = { ...prefs, [key]: value };
    updateUser({ preferences: { ...user.preferences, notifications: next } });
    mutation.mutate(next);
  };

  const items = [
    ['jobCompleted', 'Crawl selesai', 'Kirim email saat job selesai.'],
    ['jobFailed', 'Crawl gagal', 'Kirim email saat job gagal.'],
    ['quotaAlerts', 'Peringatan kuota', 'Beri tahu saat kuota hampir habis.'],
    ['weeklyDigest', 'Ringkasan mingguan', 'Rekap performa tiap Senin.'],
  ];

  return (
    <Card>
      <CardHeader title="Notifikasi" />
      <CardBody className="space-y-5">
        {items.map(([key, label, description]) => (
          <Toggle
            key={key}
            checked={Boolean(prefs[key])}
            onChange={(v) => update(key, v)}
            label={label}
            description={description}
          />
        ))}
      </CardBody>
    </Card>
  );
};

const ApiKeySection = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);

  const { data } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get('/api-keys'),
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () => api.post('/api-keys', { name: 'Kunci Utama' }),
    onSuccess: (response) => {
      // Kunci penuh hanya ada di respons ini - setelah halaman ditutup,
      // ia tidak bisa dilihat lagi.
      setRevealedKey(response.data.key);
      setConfirmRegenerate(false);
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('Kunci API dibuat', {
        description: 'Salin sekarang - kunci ini tidak akan ditampilkan lagi.',
      });
    },
    onError: (err) => toast.error('Gagal membuat kunci', { description: err.message }),
  });

  const keys = data?.data ?? [];

  return (
    <>
      <Card>
        <CardHeader
          title="API Key"
          description="Kunci ini dipakai program lain untuk menarik data crawl Anda tanpa perlu masuk. Perlakukan seperti kata sandi."
          action={
            <Button
              variant="secondary"
              size="sm"
              icon={RefreshCw}
              onClick={() => setConfirmRegenerate(true)}
            >
              Buat Ulang
            </Button>
          }
        />
        <CardBody className="space-y-3">
          {revealedKey && (
            <div className="rounded-lg border border-warning-200 bg-warning-50 p-3.5 dark:border-warning-500/25 dark:bg-warning-500/10">
              <p className="text-sm font-bold text-warning-700 dark:text-warning-500">
                Salin kunci ini sekarang
              </p>
              <p className="mt-0.5 text-xs text-warning-700/90 dark:text-warning-500/90">
                Demi keamanan, kami hanya menyimpan versi ter-hash. Kunci penuh tidak bisa
                ditampilkan kembali.
              </p>
              <div className="mt-2.5 flex items-center gap-2">
                <code className="flex-1 truncate rounded-md bg-[var(--surface-raised)] px-3 py-2 font-mono text-xs">
                  {revealedKey}
                </code>
                <Button
                  size="sm"
                  icon={Copy}
                  onClick={() => {
                    navigator.clipboard.writeText(revealedKey);
                    toast.success('Kunci disalin');
                  }}
                >
                  Salin
                </Button>
              </div>
            </div>
          )}

          {keys.length === 0 && !revealedKey ? (
            <div className="flex items-center gap-3 rounded-lg bg-[var(--surface-sunken)] p-4">
              <KeyRound className="size-5 shrink-0 text-[var(--text-muted)]" aria-hidden="true" />
              <p className="text-sm text-[var(--text-muted)]">
                Belum ada kunci API. Buat satu untuk mulai mengakses data lewat API.
              </p>
            </div>
          ) : (
            keys.map((key) => (
              <div
                key={key.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-[var(--border-subtle)] px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[var(--text-primary)]">{key.name}</p>
                  <code className="font-mono text-xs text-[var(--text-muted)]">
                    {key.maskedKey}
                  </code>
                </div>
              </div>
            ))
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={confirmRegenerate}
        onClose={() => setConfirmRegenerate(false)}
        onConfirm={() => mutation.mutate()}
        loading={mutation.isPending}
        destructive
        title="Buat ulang kunci API?"
        description="Kunci lama akan langsung berhenti berfungsi. Semua integrasi yang memakainya harus diperbarui."
        confirmLabel="Ya, buat ulang"
      />
    </>
  );
};

/**
 * Ringkasan peramban/perangkat yang sedang memegang sesi.
 *
 * User-agent mentah terlalu panjang untuk dibaca sekilas, jadi diringkas
 * menjadi nama peramban dan sistem operasinya saja. Ini murni kosmetik: yang
 * menentukan identitas sesi tetap ID-nya, bukan teks ini.
 */
const ringkasPerangkat = (userAgent = '') => {
  const peramban =
    /Edg\//.test(userAgent) ? 'Edge'
    : /OPR\//.test(userAgent) ? 'Opera'
    : /Firefox\//.test(userAgent) ? 'Firefox'
    : /Chrome\//.test(userAgent) ? 'Chrome'
    : /Safari\//.test(userAgent) ? 'Safari'
    : 'Peramban lain';

  const sistem =
    /Windows/.test(userAgent) ? 'Windows'
    : /Android/.test(userAgent) ? 'Android'
    : /iPhone|iPad/.test(userAgent) ? 'iOS'
    : /Mac OS X/.test(userAgent) ? 'macOS'
    : /Linux/.test(userAgent) ? 'Linux'
    : '';

  return sistem ? `${peramban} di ${sistem}` : peramban;
};

const SessionSection = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const clearSession = useAuthStore((s) => s.clearSession);

  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'sessions'],
    queryFn: () => api.get('/auth/sessions'),
  });

  const sesi = data?.data ?? [];

  const cabut = useMutation({
    mutationFn: (id) => api.delete(`/auth/sessions/${id}`),
    onSuccess: (hasil) => {
      // Mencabut sesi yang sedang dipakai berarti mengeluarkan diri sendiri;
      // menyegarkan daftar setelah itu hanya akan menghasilkan 401.
      if (hasil?.data?.wasCurrentSession) {
        clearSession();
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] });
      toast.success('Sesi dicabut', { description: 'Perangkat itu harus masuk ulang.' });
    },
    onError: (err) => toast.error('Gagal mencabut sesi', { description: err?.message }),
  });

  return (
    <Card>
      <CardHeader
        title="Sesi Aktif"
        description="Perangkat yang sedang masuk ke akun Anda. Kalau ada yang tidak Anda kenali, cabut sekarang juga."
      />
      <CardBody className="space-y-3">
        {isLoading ? (
          <p className="text-sm text-[var(--text-muted)]">Memuat sesi…</p>
        ) : sesi.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            compact
            title="Tidak ada sesi lain"
            description="Hanya perangkat ini yang sedang masuk."
          />
        ) : (
          sesi.map((s) => (
            <div
              key={s.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--border-subtle)] px-4 py-3"
            >
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]">
                  {ringkasPerangkat(s.userAgent)}
                  {s.isCurrent && (
                    <span className="rounded-full bg-success-50 px-2 py-0.5 text-[10px] font-bold text-success-700 dark:bg-success-500/15 dark:text-success-500">
                      Perangkat ini
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                  {s.ip || 'IP tidak tercatat'} · masuk {formatRelative(s.createdAt)} · berlaku
                  sampai {formatDateTime(s.expiresAt)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                icon={LogOut}
                loading={cabut.isPending && cabut.variables === s.id}
                onClick={() => cabut.mutate(s.id)}
              >
                Cabut
              </Button>
            </div>
          ))
        )}
      </CardBody>
    </Card>
  );
};

/**
 * Ajakan memverifikasi email, muncul hanya selama akun belum terverifikasi.
 *
 * Pengiriman email belum tersambung, jadi di luar produksi tokennya
 * dikembalikan langsung supaya alurnya bisa diselesaikan. Menyembunyikan
 * keadaan itu akan membuat pengguna menunggu email yang tidak pernah datang.
 */
const VerifikasiEmail = () => {
  const { user, updateUser } = useAuthStore();
  const toast = useToast();
  const [token, setToken] = useState(null);

  const minta = useMutation({
    mutationFn: () => api.post('/auth/verify-email/request'),
    onSuccess: (hasil) => {
      setToken(hasil?.data?.devToken ?? null);
      toast.success('Tautan verifikasi dikirim', {
        description: 'Cek kotak masuk email Anda.',
      });
    },
    onError: (err) => toast.error('Gagal mengirim tautan', { description: err?.message }),
  });

  const konfirmasi = useMutation({
    mutationFn: () => api.post('/auth/verify-email/confirm', { token }),
    onSuccess: (hasil) => {
      updateUser(hasil.data.user);
      setToken(null);
      toast.success('Email terverifikasi');
    },
    onError: (err) => toast.error('Verifikasi gagal', { description: err?.message }),
  });

  if (user?.emailVerifiedAt) return null;

  return (
    <div className="rounded-lg border border-warning-200 bg-warning-50 p-4 dark:border-warning-500/25 dark:bg-warning-500/10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <MailCheck
            className="mt-0.5 size-5 shrink-0 text-warning-600 dark:text-warning-500"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[var(--text-primary)]">
              Email belum diverifikasi
            </p>
            <p className="mt-0.5 text-xs text-[var(--text-secondary)]">
              Verifikasi diperlukan sebelum pemberitahuan lewat email bisa dikirim ke{' '}
              {user?.email}.
            </p>
          </div>
        </div>
        <Button size="sm" variant="secondary" loading={minta.isPending} onClick={() => minta.mutate()}>
          Kirim tautan
        </Button>
      </div>

      {token && (
        <div className="mt-3 border-t border-warning-200 pt-3 dark:border-warning-500/25">
          <p className="text-xs text-[var(--text-secondary)]">
            Mode pengembangan - SMTP belum aktif. Token diterbitkan langsung:
          </p>
          <Button
            size="sm"
            className="mt-2"
            loading={konfirmasi.isPending}
            onClick={() => konfirmasi.mutate()}
          >
            Verifikasi sekarang
          </Button>
        </div>
      )}
    </div>
  );
};


/**
 * Daftar akun AI. Bisa lebih dari satu, masing-masing bernama.
 *
 * Satu kunci tanpa nama tidak bisa dibedakan dari kunci lain milik akun yang
 * sama, dan pemiliknya sendiri lupa yang mana setelah beberapa minggu. Nama
 * dan keterangan karena itu punya tempat sendiri, bukan sekadar catatan.
 *
 * Kunci tidak pernah dikirim balik utuh dari server; yang tampil hanya empat
 * huruf terakhirnya sebagai bukti ia tersimpan.
 */
const AiKeySection = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [sunting, setSunting] = useState(null);
  const [hapus, setHapus] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['ai-accounts'],
    queryFn: () => api.get('/me/ai-accounts'),
  });
  const akun = data?.data ?? [];

  const segarkan = () => queryClient.invalidateQueries({ queryKey: ['ai-accounts'] });
  const galat = (err) =>
    toast.error('Gagal', {
      description: err instanceof ApiError ? err.message : 'Coba lagi.',
    });

  const simpan = useMutation({
    mutationFn: (body) =>
      body.id ? api.patch(`/me/ai-accounts/${body.id}`, body) : api.post('/me/ai-accounts', body),
    onSuccess: () => {
      toast.success('Akun AI tersimpan');
      setSunting(null);
      segarkan();
    },
    onError: galat,
  });

  const buang = useMutation({
    mutationFn: (id) => api.delete(`/me/ai-accounts/${id}`),
    onSuccess: () => {
      toast.success('Akun AI dihapus');
      setHapus(null);
      segarkan();
    },
    onError: galat,
  });

  const jadikanUtama = useMutation({
    mutationFn: (id) => api.patch(`/me/ai-accounts/${id}`, { utama: true }),
    onSuccess: segarkan,
    onError: galat,
  });

  return (
    <Card>
      <CardHeader
        title="Akun AI"
        description="Boleh lebih dari satu. Yang bertanda Utama dipakai kalau Anda tidak memilih apa pun saat menekan Deteksi."
        action={
          <Button size="sm" icon={Plus} onClick={() => setSunting({})}>
            Tambah
          </Button>
        }
      />
      <CardBody className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-[var(--text-muted)]">Memuat...</p>
        ) : akun.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[var(--border-subtle)] p-6 text-center">
            <BrainCircuit className="mx-auto size-6 text-[var(--text-muted)]" aria-hidden="true" />
            <p className="mt-2 text-sm font-semibold text-[var(--text-primary)]">
              Belum ada akun AI
            </p>
            <p className="mt-1 text-xs text-[var(--text-muted)]">
              Tambahkan satu untuk memakai mode AI saat mendeteksi kolom. Kuncinya milik Anda
              sendiri, jadi biaya pemakaiannya juga ditanggung akun Anda di penyedia itu.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {akun.map((a) => (
              <div
                key={a.id}
                className="flex flex-wrap items-start gap-3 rounded-lg border border-[var(--border-subtle)] p-3.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-[var(--text-primary)]">{a.nama}</span>
                    {a.utama && (
                      <Badge tone="brand" size="sm">
                        Utama
                      </Badge>
                    )}
                    {!a.aktif && (
                      <Badge tone="neutral" size="sm">
                        Nonaktif
                      </Badge>
                    )}
                  </p>
                  {a.deskripsi && (
                    <p className="mt-0.5 text-xs text-[var(--text-secondary)]">{a.deskripsi}</p>
                  )}
                  <p className="mt-1 font-mono text-[11px] text-[var(--text-muted)]">
                    {a.bentuk} · {a.model || 'model bawaan'} · kunci {a.kunciJejak}
                    {a.jumlahDipakai > 0 ? ` · dipakai ${a.jumlahDipakai}x` : ''}
                  </p>
                </div>

                <div className="flex shrink-0 gap-1">
                  {!a.utama && (
                    <Button
                      size="xs"
                      variant="ghost"
                      loading={jadikanUtama.isPending}
                      onClick={() => jadikanUtama.mutate(a.id)}
                    >
                      Jadikan utama
                    </Button>
                  )}
                  <Button size="xs" variant="ghost" onClick={() => setSunting(a)}>
                    Ubah
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    icon={Trash2}
                    iconOnly
                    aria-label={`Hapus ${a.nama}`}
                    title="Hapus akun AI ini"
                    onClick={() => setHapus(a)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-[var(--text-muted)]">
          Kunci disimpan di akun Anda dan hanya dipakai saat Anda menekan Deteksi pada mode AI. Ia
          tidak pernah ditampilkan utuh dan tidak pernah masuk log. Isi halaman yang Anda scrape
          juga tidak pernah dikirim ke penyedia mana pun, hanya nama tebakan kolom dan beberapa
          contoh nilai.
        </p>
      </CardBody>

      <Modal
        open={Boolean(sunting)}
        onClose={() => setSunting(null)}
        size="lg"
        title={sunting?.id ? `Ubah ${sunting.nama}` : 'Tambah akun AI'}
      >
        {sunting && (
          <FormAkunAi
            awal={sunting}
            menyimpan={simpan.isPending}
            onBatal={() => setSunting(null)}
            onSimpan={(body) => simpan.mutate({ ...body, id: sunting.id })}
          />
        )}
      </Modal>

      <ConfirmDialog
        open={Boolean(hapus)}
        onClose={() => setHapus(null)}
        onConfirm={() => buang.mutate(hapus.id)}
        loading={buang.isPending}
        destructive
        title={`Hapus ${hapus?.nama}?`}
        confirmLabel="Hapus"
        description="Job yang sudah selesai tidak terpengaruh. Kalau ini akun utama Anda, akun lain akan naik menggantikannya."
      />
    </Card>
  );
};

/** Formulir satu akun AI. */
